/* ============================================================================
   <unc-bt-outcome>
   Everything after the drinks on the calculator page (v2, 4 Oct 2026,
   @marketing W5 to W15): the ghost card, the cutoff card, the 7-Day Cutoff
   Plan (the email ask, sent in place), the thank-you state with the calendar
   reminder and share, the phone bar, and the folded breakdown + product card
   that this element wraps.

   IT LISTENS, IT DOES NOT REACH IN. The only coupling to the calculator is the
   `unc:calc` event dispatched at the end of <unc-bedtime>.renderOut(), plus the
   hidden bedtime slider it writes into. With the section's funnel switch off,
   none of this markup renders and this never runs.

   COPY LIVES IN THE MARKUP, not here. Every sentence is a theme setting with
   {tokens}; this file only substitutes. Snippet: snippets/unc-bt-ask.liquid.

   Requires assets/unc-calc.js.
   ============================================================================ */
(function () {
  'use strict';

  /* Bands on daily total, for the customer tag only (calc-band-N). Edges are
     `max` inclusive, so 250 is band 1 and 251 is band 2. */
  var BANDS = [
    { id: 0, max: 99 },
    { id: 1, max: 250 },
    { id: 2, max: 400 },
    { id: 3, max: Infinity },
  ];

  // One drink has to own this much of the bedtime figure before it is named in
  // the breakdown, and the figure has to be big enough for a share to mean much.
  var OFFENDER_SHARE = 0.5; // "most of": more than half (C6, 9 Oct; was 0.35)
  var OFFENDER_FLOOR = 12;

  var REVEAL_MS = 400;
  var STORE_KEY = 'uncCalcDay';
  var NAME_KEY = 'uncCalcName';
  var UNLOCK_KEY = 'uncCalcUnlocked';

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --------------------------------------------------------------------------
     tracking

     Through window.uncTrack (assets/unc-track.js: dataLayer, Shopify.analytics
     and GA4 via the Google app's own gtag). Events, all `unc_calc_*`:
       view, first_drink, recalculated, result_view, ask_view, outcome_view,
       email_submit, email, share, product_click,
       plan_days_open, breakdown_open, reminder_add (new in v2).
     Meta (window.uncMeta): CalculatorResult {mg_bucket} once the total has
     settled 2s and only when the bucket changes; Lead on a successful sign-up.
     -------------------------------------------------------------------------- */
  var lastBucket = null;
  var bucketTimer = null;
  function mgBucket(total) {
    if (total < 200) return '<200';
    if (total <= 400) return '200-400';
    return '400+';
  }
  function toMeta(name, payload) {
    if (typeof window.uncMeta !== 'function') return;
    try {
      if (name === 'result') {
        var total = payload && payload.total;
        if (typeof total !== 'number') return;
        var b = mgBucket(total);
        clearTimeout(bucketTimer);
        bucketTimer = setTimeout(function () {
          if (b === lastBucket) return;
          lastBucket = b;
          window.uncMeta('CalculatorResult', { mg_bucket: b }, true);
        }, 2000);
      } else if (name === 'email') {
        window.uncMeta('Lead', { content_name: 'calculator' });
      }
    } catch (e) {}
  }

  function emit(name, payload) {
    var ev = 'unc_calc_' + name;
    if (typeof window.uncTrack === 'function') {
      window.uncTrack(ev, payload || {});
      return;
    }
    try {
      window.dataLayer = window.dataLayer || [];
      var row = { event: ev };
      for (var k in payload) if (payload.hasOwnProperty(k)) row[k] = payload[k];
      window.dataLayer.push(row);
    } catch (e) {}
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish(ev, payload);
      }
    } catch (e) {}
  }

  /* --------------------------------------------------------------------------
     helpers
     -------------------------------------------------------------------------- */
  function mins(h) {
    var m = Math.round(h * 60) % 1440;
    return m < 0 ? m + 1440 : m;
  }
  // 14.2 -> "2:12pm" (the cutoff)
  function clock12(h) {
    var m = mins(h);
    var hh = Math.floor(m / 60);
    var mm = m % 60;
    return (hh % 12 || 12) + ':' + (mm < 10 ? '0' : '') + mm + (hh < 12 ? 'am' : 'pm');
  }
  // 23 -> "11pm", 23.5 -> "11:30pm" (bedtimes, as people say them)
  function clockShort(h) {
    var m = mins(h);
    var hh = Math.floor(m / 60);
    var mm = m % 60;
    return (hh % 12 || 12) + (mm ? ':' + (mm < 10 ? '0' : '') + mm : '') + (hh < 12 ? 'am' : 'pm');
  }
  // 14.2 -> "14:12" (the customer note and tag)
  function clock24(h) {
    var m = mins(h);
    var hh = Math.floor(m / 60);
    var mm = m % 60;
    return (hh < 10 ? '0' : '') + hh + ':' + (mm < 10 ? '0' : '') + mm;
  }
  // "a 10pm bedtime", "an 11pm bedtime", "an 8pm bedtime"
  function article(h) {
    var h12 = Math.floor(mins(h) / 60) % 12 || 12;
    return h12 === 8 || h12 === 11 ? 'an' : 'a';
  }
  // 100mg a cup of coffee (FACTS): 55 -> "½ a cup", 120 -> "1 cup", 160 -> "1½ cups"
  function cupsText(mg) {
    var q = Math.round((mg / 100) * 4) / 4;
    var whole = Math.floor(q);
    var frac = ['', '\u00bc', '\u00bd', '\u00be'][Math.round((q - whole) * 4)];
    if (whole === 0) return frac === '\u00bd' ? '\u00bd a cup' : frac + ' of a cup';
    return whole + frac + (q > 1 ? ' cups' : ' cup');
  }

  function esc(v) {
    return String(v).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  /* Substitutes {token} against a plain object. An unknown token is left alone,
     so a typo in a theme setting shows up on the page instead of vanishing. */
  function fill(tpl, vars) {
    return String(tpl).replace(/\{(\w+)\}/g, function (whole, key) {
      return vars.hasOwnProperty(key) ? vars[key] : whole;
    });
  }

  /* Where the visitor came from, as a customer tag (src-ig, src-meta,
     src-share), from utm_source on the landing URL. Kept for the session. */
  function sourceTag() {
    var src = '';
    try {
      src = (new URLSearchParams(window.location.search).get('utm_source') || '').toLowerCase();
      if (src) sessionStorage.setItem('uncSrc', src);
      else src = sessionStorage.getItem('uncSrc') || '';
    } catch (e) {}
    if (/^(ig|insta)/.test(src)) return 'src-ig';
    if (/^(fb|facebook|meta)/.test(src)) return 'src-meta';
    if (src === 'share') return 'src-share';
    return '';
  }

  function bandFor(total) {
    for (var i = 0; i < BANDS.length; i++) {
      if (total <= BANDS[i].max) return BANDS[i].id;
    }
    return BANDS[BANDS.length - 1].id;
  }

  // Local "floating" time for the calendar file: 20261004T141200
  function icsLocal(dt) {
    var p = function (n) {
      return (n < 10 ? '0' : '') + n;
    };
    return dt.getFullYear() + p(dt.getMonth() + 1) + p(dt.getDate()) + 'T' + p(dt.getHours()) + p(dt.getMinutes()) + '00';
  }
  function icsText(s) {
    return String(s).replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
  }

  if (customElements.get('unc-bt-outcome')) return;

  customElements.define(
    'unc-bt-outcome',
    class UncBtOutcome extends HTMLElement {
      connectedCallback() {
        this.revealed = false;
        this.band = null;
        this.completed = false;
        this.signedUp = false;

        this.onCalc = this.onCalc.bind(this);
        this.onClick = this.onClick.bind(this);
        document.addEventListener('unc:calc', this.onCalc);
        this.addEventListener('click', this.onClick);

        // The generic ending is a no-JS fallback only; this retires it.
        var sec = this.closest('[data-bt-section]');
        if (sec) sec.classList.add('has-funnel');

        this.host = document.querySelector('unc-bedtime');
        this.initCard();
        emit('view', {});
        this.restoreAfterPost();
      }

      disconnectedCallback() {
        document.removeEventListener('unc:calc', this.onCalc);
        this.removeEventListener('click', this.onClick);
        if (this.io) this.io.disconnect();
      }

      /* ---------------------------------------------------------------------
         surviving the full POST (the F5 fallback, or no fetch)

         A {% form 'customer' %} POST is a real navigation. The day is stashed
         on submit and put back here, and a successful return shows the
         thank-you state, so nobody loses their result at the moment they
         signed up.
      --------------------------------------------------------------------- */
      restoreAfterPost() {
        this.leadAfterPost();
        var posted =
          window.location.search.indexOf('customer_posted=true') !== -1 &&
          window.location.hash === '#unc-bto-signup';
        var raw = null;
        try {
          raw = sessionStorage.getItem(STORE_KEY);
          sessionStorage.removeItem(STORE_KEY);
        } catch (e) {}
        var saved = null;
        try {
          saved = raw ? JSON.parse(raw) : null;
        } catch (e) {}

        // Not gated on success: a rejected email re-renders the page with
        // form.errors, and a typo must not cost them their logged day.
        var fresh = saved && Date.now() - (saved.at || 0) >= 0 && Date.now() - (saved.at || 0) <= 120000;

        var self = this;
        var go = function () {
          var restored = fresh && self.host && typeof self.host.restore === 'function' && self.host.restore(saved.day, saved.bedtime);
          if (restored && posted && saved.band != null) emit('email', { band: saved.band });
          if (posted) {
            self.showDone(self.storedName(), !restored);
            self.unlock(false);
          }
          if (!restored && !posted) return;
          // Land on their result with the confirmation, not on the browser's guess.
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              var el = posted ? self.ask : self.card;
              if (!el || el.hidden) return;
              var box = el.getBoundingClientRect();
              var target = window.scrollY + box.top - Math.max(24, window.innerHeight * 0.12);
              window.scrollTo({ top: Math.max(0, target), behavior: 'auto' });
            });
          });
        };
        if (this.host && this.host.drinks) go();
        else customElements.whenDefined('unc-bedtime').then(go);
      }

      /* Meta Lead, once per successful full-POST sign-up (the in-place send
         fires it from sentInPlace). Shopify returns a successful customer form
         to ?customer_posted=true#<form id>. A flag stops a refresh resending. */
      leadAfterPost() {
        if (window.location.search.indexOf('customer_posted=true') === -1) return;
        if (window.location.hash !== '#unc-bto-signup') return;
        try {
          if (sessionStorage.getItem('uncCalcLead') === window.location.href) return;
          sessionStorage.setItem('uncCalcLead', window.location.href);
        } catch (e) {}
        toMeta('email');
      }

      saveForPost() {
        try {
          var name = this.form && this.form.querySelector('[name="contact[first_name]"]');
          if (name) sessionStorage.setItem(NAME_KEY, name.value.trim());
        } catch (e) {}
        if (!this.host || typeof this.host.dayspec !== 'function') return;
        try {
          sessionStorage.setItem(
            STORE_KEY,
            JSON.stringify({
              day: this.host.dayspec(),
              bedtime: this.host.bedtime,
              band: this.band,
              at: Date.now(),
            })
          );
        } catch (e) {}
      }

      storedName() {
        try {
          return sessionStorage.getItem(NAME_KEY) || '';
        } catch (e) {
          return '';
        }
      }

      /* ---------------------------------------------------------------------
         F5: sending the email without leaving the page (W8)

         Shopify's captcha scripts stop every customer-form submit, run the
         invisible hCaptcha, add h-captcha-response and call form.submit().
         initCard replaces form.submit before they bind, so this runs last,
         token included.

         SUCCESS IS STRICT: only a response that ended on ?customer_posted=true.
         Anything else (error page, /challenge, no network, 15s) falls back to
         the real POST. Never loosen this check.
      --------------------------------------------------------------------- */
      sendInPlace() {
        var self = this;
        var form = this.form;
        if (!form || this.sending) return;
        var btn = form.querySelector('[data-bto-submit]');
        var fallback = function () {
          self.sending = false;
          if (btn) {
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
          }
          self.nativeSubmit.call(form);
        };
        if (!window.fetch || !window.FormData || !window.URLSearchParams) return fallback();

        var body;
        try {
          body = new URLSearchParams(new FormData(form));
        } catch (e) {
          return fallback();
        }
        this.sending = true;
        if (btn) {
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
        }
        var ctrl = window.AbortController ? new AbortController() : null;
        var timer = setTimeout(function () {
          if (ctrl) ctrl.abort();
        }, 15000);
        fetch(form.action, {
          method: 'POST',
          body: body,
          credentials: 'same-origin',
          redirect: 'follow',
          signal: ctrl ? ctrl.signal : undefined,
        }).then(
          function (res) {
            clearTimeout(timer);
            if (res.ok && /[?&]customer_posted=true(&|#|$)/.test(res.url)) self.sentInPlace();
            else fallback();
          },
          function () {
            clearTimeout(timer);
            fallback();
          }
        );
      }

      sentInPlace() {
        this.sending = false;
        try {
          sessionStorage.removeItem(STORE_KEY);
        } catch (e) {}
        emit('email', { band: this.band });
        toMeta('email');
        this.showDone(this.storedName(), false);
        this.unlock(true);
      }

      /* ---------------------------------------------------------------------
         the unlock (20 Oct, @marketing): the bedtime chart is blurred under one
         real line until the plan form is sent. The cutoff and the number are free.
      --------------------------------------------------------------------- */
      isLocked() {
        return !!this.peek && this.peek.dataset.locked === 'true';
      }

      renderLock(d) {
        if (!this.peek || !this.lockLine || !this.isLocked()) return;
        var tpl = d.residual < 10 ? this.peek.dataset.lockLineNone : this.peek.dataset.lockLine;
        var line = fill(tpl || '', { bedtime: clockShort(d.bedtime), amount: cupsText(d.residual) });
        this.lockLine.textContent = line;
      }

      unlock(fromSubmit) {
        try {
          localStorage.setItem(UNLOCK_KEY, '1');
        } catch (e) {}
        if (!this.peek) return;
        var was = this.isLocked();
        this.peek.dataset.locked = 'false';
        if (typeof this.setFold === 'function') this.setFold(true);
        if (was && fromSubmit) emit('unlock', { band: this.band });
        if (fromSubmit) {
          var peek = this.peek;
          requestAnimationFrame(function () {
            try {
              peek.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
            } catch (e) {}
          });
        }
      }

      /* W9: the plan card becomes the thank-you state, in place. */
      showDone(name, noCutoff) {
        if (!this.ask) return;
        this.signedUp = true;
        this.ask.hidden = false;
        var offer = this.ask.querySelector('[data-plan-offer]');
        var done = this.ask.querySelector('[data-plan-done]');
        if (offer) offer.hidden = true;
        if (!done) return;
        done.hidden = false;
        var slot = done.querySelector('[data-plan-name]');
        if (slot) slot.textContent = name || '';
        var head = slot && slot.parentNode;
        // No name to hand (a very slow round trip): "Day 1 is on its way."
        if (head && !name) head.innerHTML = head.innerHTML.replace(/,\s*<span data-plan-name=""><\/span>/, '');
        if (this.remind) this.remind.hidden = !!noCutoff || !this.lastCalc;
        if (this.share) this.share.hidden = !this.shareCut;
        try {
          done.focus({ preventScroll: true });
        } catch (e) {}
        if (!noCutoff) {
          var r = done.getBoundingClientRect();
          if (r.top < 0 || r.bottom > window.innerHeight) {
            done.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
          }
        }
        this.syncBar();
      }

      /* ---------------------------------------------------------------------
         the result
      --------------------------------------------------------------------- */
      onCalc(e) {
        var d = e.detail;
        if (!d) return;

        var band = bandFor(d.total);
        this.updateCard(d, band);

        // The server's default day is not their day: nothing until they act.
        if (!d.touched) return;

        if (!d.drinks.length) {
          this.retreat();
          return;
        }

        if (!this.completed) {
          this.completed = true;
          emit('first_drink', {
            band: band,
            total: d.total,
            residual: d.residual,
            drinks: d.drinks.length,
          });
          this.revealOnFirst();
        } else if (band !== this.band) {
          emit('recalculated', { band_from: this.band, band_to: band });
        }

        toMeta('result', { total: d.total });

        this.band = band;
        this.dataset.band = band;
        this.renderSlots(d);
        this.renderLock(d);
        this.syncTags();
        if (!this.revealed) this.reveal();
        if (this.isLocked()) this.once('unlock_view');
      }

      /* The breakdown's "x% of what is left" line. Measured against the
         unrounded pool, so a lone drink can never be 101% of what is left. */
      renderSlots(d) {
        var top = d.contributions[0] || null;
        var pool = 0;
        for (var i = 0; i < d.contributions.length; i++) pool += d.contributions[i].at;
        var share = top && d.residual >= OFFENDER_FLOOR && pool > 0 ? top.at / pool : 0;
        // C6 (9 Oct): no line for a single drink ("100%" says nothing), and
        // "most of" only when it really is more than half.
        var offender = share >= OFFENDER_SHARE && d.contributions.length >= 2 ? top : null;
        var vars = {
          drink: offender ? this.tileLabel(offender.n) : '',
          drink_time: offender ? clockShort(offender.hour) : '',
          drink_pct: offender ? Math.min(100, Math.round(share * 100)) : 0,
          total: d.total,
          residual: d.residual,
          peak: d.peak,
          bedtime: clockShort(d.bedtime),
        };
        Array.prototype.forEach.call(this.querySelectorAll('[data-slot]'), function (el) {
          if (el._tpl === undefined) el._tpl = el.textContent;
          var on = el.dataset.slot === 'offender' ? !!offender : true;
          el.hidden = !on;
          if (on) el.textContent = fill(el._tpl, vars);
        });
      }

      /* calc, calc-band-N, plan7, src-*, cutoff-HHMM (W7). Written whenever the
         result changes and again on submit. */
      syncTags() {
        var input = this.form && this.form.querySelector('[data-bto-tags]');
        if (!input) return;
        var tags = ['calc', 'calc-band-' + (this.band == null ? 0 : this.band), 'plan7', 'ask-unlock'];
        var src = sourceTag();
        if (src) tags.push(src);
        if (this.lastCalc) tags.push('cutoff-' + clock24(this.cutoffOf(this.lastCalc)).replace(':', ''));
        input.value = tags.join(',');
      }

      /* ---------------------------------------------------------------------
         reveal of the breakdown + product card
      --------------------------------------------------------------------- */
      reveal() {
        this.revealed = true;
        this.hidden = false;
        this.watchView();
        if (reduced) return;

        var self = this;
        var h = this.scrollHeight;
        this.style.height = '0px';
        this.style.opacity = '0';
        this.style.overflow = 'hidden';
        void this.offsetHeight;
        this.style.transition = 'height ' + REVEAL_MS + 'ms ease-out, opacity ' + REVEAL_MS + 'ms ease-out';
        this.style.height = h + 'px';
        this.style.opacity = '1';
        setTimeout(function () {
          self.style.transition = '';
          self.style.height = '';
          self.style.opacity = '';
          self.style.overflow = '';
        }, REVEAL_MS + 20);
      }

      retreat() {
        if (!this.revealed) return;
        this.revealed = false;
        this.hidden = true;
      }

      watchView() {
        if (this.io || !('IntersectionObserver' in window)) return;
        var self = this;
        this.io = new IntersectionObserver(
          function (entries) {
            if (!entries[0].isIntersecting) return;
            emit('outcome_view', { band: self.band });
            self.io.disconnect();
            self.io = null;
          },
          { threshold: 0.25 }
        );
        this.io.observe(this);
      }

      onClick(e) {
        var link = e.target.closest('[data-bto-product]');
        if (link && this.contains(link)) {
          emit('product_click', { band: this.band, placement: link.dataset.btoProduct });
        }
      }

      /* ---------------------------------------------------------------------
         the cards, the form and the bar
      --------------------------------------------------------------------- */
      initCard() {
        var self = this;
        this.ghost = document.querySelector('[data-cut-ghost]');
        this.card = document.querySelector('[data-cut-card]');
        this.ask = document.querySelector('[data-cut-ask]');
        this.bar = document.querySelector('[data-cut-bar]');
        this.form = document.getElementById('unc-bto-signup');
        this.remind = document.querySelector('[data-plan-remind]');
        this.share = document.querySelector('[data-cut-share]');
        this.cardOnScreen = false;
        this.lastCalc = null;

        this.peek = this.querySelector('[data-bt-breakdown]');
        this.lockLine = this.querySelector('[data-bt-lock-line]');
        // A returning visitor who already unlocked: chart open, no second ask.
        this.unlockedReturn = false;
        try {
          this.unlockedReturn = localStorage.getItem(UNLOCK_KEY) === '1';
        } catch (e) {}
        if (this.unlockedReturn && this.peek) this.peek.dataset.locked = 'false';

        // The six quick tiles show their mg from the same data the calculator
        // adds, so a tile can never promise a different number.
        var db = window.UNC_CAFFEINE_DB;
        var rows = db ? db.rows || db : [];
        if (!Array.isArray(rows)) rows = Object.keys(rows).map(function (k) { return rows[k]; });
        Array.prototype.forEach.call(document.querySelectorAll('.unc-bt__qt[data-bt-add]'), function (b) {
          var name = (b.dataset.btAdd || '').toLowerCase();
          var row = rows.filter(function (r) { return r && r.n && r.n.toLowerCase() === name; })[0];
          var out = b.querySelector('[data-bt-quick-mg]');
          if (row && out) out.textContent = row.mg + 'mg';
        });

        // W4: the card's bedtime picker drives the calculator's hidden slider.
        var sel = this.card && this.card.querySelector('[data-cut-bed]');
        if (sel) {
          sel.addEventListener('change', function () {
            var slider = document.querySelector('[data-bt-bedtime]');
            if (!slider) return;
            slider.value = sel.value;
            slider.dispatchEvent(new Event('input', { bubbles: true }));
          });
        }

        if (this.share) {
          this.share.addEventListener('click', function () {
            self.doShare();
          });
        }
        if (this.remind) {
          this.remind.addEventListener('click', function () {
            self.addReminder();
          });
        }

        // W14: the bar takes them to the plan.
        if (this.bar) {
          this.bar.addEventListener('click', function () {
            self.goToForm();
          });
        }

        var days = document.querySelector('[data-plan-days]');
        if (days) {
          days.addEventListener('toggle', function () {
            if (days.open) self.once('plan_days_open');
          });
        }
        /* The breakdown peek: closed shows the top of the chart under a fade.
           The button opens and closes it; a tap on the faded peek opens it. */
        var fold = this.querySelector('[data-bt-breakdown]');
        var foldBtn = fold && fold.querySelector('[data-bt-peek-btn]');
        if (foldBtn) {
          var foldLabel = foldBtn.querySelector('[data-bt-peek-label]');
          var setFold = (this.setFold = function (open) {
            fold.dataset.open = open ? 'true' : 'false';
            foldBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
            if (foldLabel) {
              foldLabel.textContent = open
                ? foldBtn.dataset.closeLabel || foldBtn.dataset.openLabel
                : foldBtn.dataset.openLabel;
            }
            if (open) self.once('breakdown_open');
          });
          if (this.unlockedReturn) setFold(true);
          foldBtn.addEventListener('click', function () {
            var opening = fold.dataset.open !== 'true';
            setFold(opening);
            // Closing from the bottom of a long breakdown: bring the peek back into view.
            if (!opening) {
              var r = fold.getBoundingClientRect();
              if (r.top < 0) fold.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
            }
          });
          var body = fold.querySelector('[data-bt-peek-body]');
          if (body) {
            body.addEventListener('click', function () {
              if (fold.dataset.open !== 'true') setFold(true);
            });
          }
        }

        if (this.form) {
          this.nativeSubmit = HTMLFormElement.prototype.submit;
          /* 7 Oct: with Shopify's hCaptcha on the page the form goes as a normal
             full-page POST. The captcha's token is single-use: the in-place
             send used it, and its fallback re-posted the same token, which
             Shopify rejects ("Invalid CAPTCHA token", no customer). The full
             POST comes back to ?customer_posted=true and restoreAfterPost()
             shows the thank-you. The in-place send stays for pages without
             the captcha (and the local harness). */
          this.captcha = !!document.getElementById('captcha-bootstrap');
          if (!this.captcha) {
            try {
              this.form.submit = function () {
                self.sendInPlace();
              };
            } catch (e) {}
          }
          this.form.addEventListener('submit', function (ev) {
            self.saveForPost();
            self.writeNote();
            self.syncTags();
            emit('email_submit', { band: self.band });
            // Without Shopify's captcha nothing else will call form.submit().
            if (!self.captcha) {
              ev.preventDefault();
              self.sendInPlace();
            }
          });
          // A typing visitor does not need the bar over the keyboard.
          this.form.addEventListener('focusin', function () {
            self.formFocus = true;
            self.syncBar();
          });
          this.form.addEventListener('focusout', function () {
            self.formFocus = false;
            self.syncBar();
          });
        }

        if ('IntersectionObserver' in window) {
          if (this.card) {
            new IntersectionObserver(function (entries) {
              self.cardOnScreen = entries[0].isIntersecting;
              if (self.cardOnScreen && !self.card.hidden) self.once('result_view');
              self.syncBar();
            }).observe(this.card);
          }
          if (this.ask) {
            // "Seen" = half of it, or 200px of it when it is taller than that.
            new IntersectionObserver(
              function (entries) {
                var en = entries[0];
                if (!en.isIntersecting || self.ask.hidden) return;
                if (en.intersectionRect.height >= Math.min(200, en.boundingClientRect.height * 0.5)) self.once('ask_view');
              },
              { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5] }
            ).observe(this.ask);
          }
        }

        // The bar hides while the plan card fills the middle of the screen.
        var ticking = false;
        window.addEventListener(
          'scroll',
          function () {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(function () {
              ticking = false;
              self.syncBar();
            });
          },
          { passive: true }
        );

        var mq = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
        this.isPhone = function () {
          return mq ? mq.matches : false;
        };
        if (mq && mq.addEventListener) mq.addEventListener('change', function () { self.syncBar(); });
      }

      once(name) {
        this.sent = this.sent || {};
        if (this.sent[name]) return;
        this.sent[name] = true;
        emit(name, { band: this.band });
      }

      updateCard(d, band) {
        if (!this.card) return;
        var has = !!(d.touched && d.drinks && d.drinks.length);
        this.lastCalc = has ? d : null;
        if (this.ghost) this.ghost.hidden = has;
        this.card.hidden = !has;
        if (this.ask && !this.signedUp && !this.unlockedReturn) this.ask.hidden = !has;
        if (!has) {
          this.syncBar();
          return;
        }

        var cut = this.cutoffOf(d);
        var cutText = clockShort(cut);
        this.shareCut = cutText;
        var t = this.card.querySelector('[data-cut-time]');
        if (t) t.textContent = cutText;
        Array.prototype.forEach.call(document.querySelectorAll('[data-cut-time-inline]'), function (el) {
          el.textContent = cutText;
        });
        var a = this.card.querySelector('[data-cut-a]');
        if (a) a.textContent = article(d.bedtime);

        /* The personal line (W6), from the calculator's own "one more standard
           coffee under 50mg at bedtime" maths:
             B  no more coffee fits: what is left at bedtime, as an everyday drink;
             A1 it fits, but only before T, earlier than the cutoff;
             A2 it fits up to the cutoff (T is never shown). */
        /* C3 (9 Oct, marketing value-signal audit): the personal line.
             LATE   a drink lands after the cutoff: what's left at bedtime, then
                    what moving that one drink to the cutoff would leave.
             B      no more coffee fits (nothing after the cutoff): unchanged.
             A2     one more fits up to the cutoff: name the cutoff.
             A1     it fits, but only earlier than the cutoff: no second time. */
        var me = this.card.querySelector('[data-cut-personal]');
        if (me) {
          var latest = this.latestOf(d);
          var ds = this.card.dataset;
          var late = this.lateDrink(d, cut);
          var tpl;
          if (late && ds.personalLate) tpl = ds.personalLate + (ds.move ? ' ' + ds.move : '');
          else tpl = latest === null ? ds.personalOver : latest >= cut ? ds.personalCut : ds.personal;
          var equiv = d.equiv ? esc(ds.equiv || '').split('{thing}').join(esc(d.equiv)) : '';
          var hl = d.halfLife || 6;
          var newMg = late ? Math.max(0, (d.residual || 0) - late.at + late.mg * Math.pow(0.5, (d.bedtime - cut) / hl)) : 0;
          me.innerHTML = esc(tpl || '')
            .split('{mg}').join('<strong>' + Math.round(d.residual || 0) + '</strong>')
            .split('{new_mg}').join('<strong>' + Math.round(newMg) + '</strong>')
            .split('{drink}').join(late ? esc(this.tileLabel(late.n)) : '')
            .split('{drink_time}').join(late ? clockShort(late.hour) : '')
            .split('{bedtime}').join(clockShort(d.bedtime))
            .split('{equiv}').join(equiv)
            .split('{latest}').join(latest === null ? '' : '<strong>' + clock12(latest) + '</strong>')
            .split('{cutoff}').join('<strong>' + cutText + '</strong>')
            .split('{threshold}').join(esc(ds.threshold || '50'));
        }

        var sel = this.card.querySelector('[data-cut-bed]');
        if (sel && d.bedtime != null) {
          var v = Math.round(d.bedtime * 2) / 2;
          var opt = Array.prototype.filter.call(sel.options, function (o) {
            return parseFloat(o.value) === v;
          })[0];
          if (opt) sel.value = opt.value;
        }

        if (this.bar) {
          var bt = this.bar.querySelector('[data-cut-bar-text]');
          if (bt) bt.innerHTML = esc(this.bar.dataset.text || '').split('{cutoff}').join('<b>' + cutText + '</b>');
        }
        if (this.band == null) this.band = band;
        this.syncTags();
        this.syncBar();
      }

      /* W9: a daily "last coffee" event at their cutoff for 7 days, as a
         calendar file made in the browser. Starts today, or tomorrow when
         today's cutoff has already gone. */
      addReminder() {
        var d = this.lastCalc;
        if (!d || !this.remind) return;
        var cut = this.cutoffOf(d);
        var m = mins(cut);
        var start = new Date();
        start.setHours(Math.floor(m / 60), m % 60, 0, 0);
        if (start.getTime() < Date.now()) start.setDate(start.getDate() + 1);
        var vars = { a: article(d.bedtime), bedtime: clockShort(d.bedtime), cutoff: clockShort(cut) };
        var title = fill(this.remind.dataset.title || 'Last coffee', vars);
        var desc = fill(this.remind.dataset.desc || '', vars);
        var now = new Date();
        var stamp = now.toISOString().replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z');
        var ics = [
          'BEGIN:VCALENDAR',
          'VERSION:2.0',
          'PRODID:-//Uncaffed//Cutoff reminder//EN',
          'CALSCALE:GREGORIAN',
          'METHOD:PUBLISH',
          'BEGIN:VEVENT',
          'UID:' + now.getTime() + '-cutoff@uncaffed.com',
          'DTSTAMP:' + stamp,
          'DTSTART:' + icsLocal(start),
          'DURATION:PT15M',
          'RRULE:FREQ=DAILY;COUNT=7',
          'SUMMARY:' + icsText(title),
          'DESCRIPTION:' + icsText(desc),
          'URL:https://uncaffed.com/calculator',
          'BEGIN:VALARM',
          'ACTION:DISPLAY',
          'DESCRIPTION:' + icsText(title),
          'TRIGGER:PT0M',
          'END:VALARM',
          'END:VEVENT',
          'END:VCALENDAR',
        ].join('\r\n');
        /* 7 Oct (Tim: "doesn't work"): the old data: link is blocked by iPhones
           and in-app browsers. Android has no .ics handler, so it gets Google
           Calendar's own add-event page. Everyone else gets the file as a Blob:
           iPhone opens it straight into Calendar, computers download it. */
        var ua = navigator.userAgent || '';
        if (/Android/i.test(ua)) {
          var end = new Date(start.getTime() + 15 * 60000);
          var g =
            'https://calendar.google.com/calendar/render?action=TEMPLATE' +
            '&text=' + encodeURIComponent(title) +
            '&details=' + encodeURIComponent(desc) +
            '&dates=' + icsLocal(start) + '/' + icsLocal(end) +
            '&recur=' + encodeURIComponent('RRULE:FREQ=DAILY;COUNT=7');
          window.open(g, '_blank', 'noopener');
          emit('reminder_add', { band: this.band, via: 'google' });
          return;
        }
        var url = null;
        try {
          url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
        } catch (e) {}
        if (!url) url = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(ics);
        var apple = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
        if (apple) {
          window.location.href = url;
        } else {
          var a = document.createElement('a');
          a.href = url;
          a.download = 'last-coffee.ics';
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        if (url.indexOf('blob:') === 0) {
          setTimeout(function () {
            URL.revokeObjectURL(url);
          }, 60000);
        }
        emit('reminder_add', { band: this.band, via: apple ? 'ics-apple' : 'ics' });
      }

      /* The phone's share sheet, or copy the text and link on computers.
         unc_calc_share fires only when it actually went. */
      doShare() {
        var b = this.share;
        if (!b || !this.shareCut) return;
        var text = (b.dataset.text || '').split('{cutoff}').join(this.shareCut);
        var url = b.dataset.url || window.location.href;
        var band = this.band;
        if (navigator.share) {
          navigator
            .share({ text: text, url: url })
            .then(function () {
              emit('share', { method: 'share', band: band });
            })
            .catch(function () {});
          return;
        }
        var label = b.querySelector('[data-cut-share-label]');
        var done = function () {
          emit('share', { method: 'copy', band: band });
          if (!label) return;
          var was = label.textContent;
          label.textContent = b.dataset.copied || was;
          setTimeout(function () {
            label.textContent = was;
          }, 2000);
        };
        try {
          navigator.clipboard.writeText(text + ' ' + url).then(done, function () {});
        } catch (e) {}
      }

      /* Capture spec #3 (6 Oct): on the FIRST drink only, on a phone, bring
         the cutoff card up to about 80px from the top so the answer and the
         email box share one screen. Later taps never move the page. */
      revealOnFirst() {
        if (!this.card || !this.isPhone || !this.isPhone()) return;
        // Back from a full-page sign-up: showDone owns the scroll.
        if (/[?&]customer_posted=true/.test(window.location.search)) return;
        var self = this;
        requestAnimationFrame(function () {
          var top = self.card.getBoundingClientRect().top;
          if (top <= 100) return;
          window.scrollTo({
            top: Math.max(0, window.pageYOffset + top - 80),
            behavior: reduced ? 'auto' : 'smooth',
          });
        });
      }

      /* Capture spec #5: the bar goes to the email box itself and focuses the
         first empty field, which opens the keyboard on phones. */
      goToForm() {
        var form = this.form;
        if (!form || !this.ask || this.ask.hidden) {
          if (this.card) this.card.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
          return;
        }
        var fields = form.querySelectorAll('.unc-bto__field');
        var first = Array.prototype.filter.call(fields, function (f) { return !f.value; })[0] || fields[0];
        form.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
        if (first) {
          try {
            first.focus({ preventScroll: true });
          } catch (e) {
            first.focus();
          }
        }
      }

      /* W14: phones only, after the first drink, never after sign-up, never
         while the form has focus, and never while the cutoff card is on screen
         (it would cover the personal line) or the plan card fills the middle
         of the screen. */
      syncBar() {
        if (!this.bar) return;
        // "In view" is the form itself, not the whole (tall) plan card.
        var planInView = false;
        var target = this.form || this.ask;
        if (this.ask && !this.ask.hidden && target) {
          var r = target.getBoundingClientRect();
          var vh = window.innerHeight || 0;
          planInView = r.top < vh * 0.85 && r.bottom > vh * 0.15;
        }
        var show =
          !!this.lastCalc &&
          !this.signedUp &&
          !this.unlockedReturn &&
          this.isPhone &&
          this.isPhone() &&
          !this.cardOnScreen &&
          !planInView &&
          !this.formFocus;
        this.bar.hidden = !show;
        document.documentElement.classList.toggle('unc-bt-has-bar', show);
        if (show) this.once('result_view');
      }

      // "Cutoff: 14:12 for 23:00 bed" in the customer note, for the emails.
      writeNote() {
        var note = this.form && this.form.querySelector('[data-bto-note]');
        var d = this.lastCalc;
        if (!note || !d) return;
        note.value = 'Cutoff: ' + clock24(this.cutoffOf(d)) + ' for ' + clock24(d.bedtime) + ' bed';
      }

      /* The headline cutoff: bedtime minus the offset (8.8h, the "about 9 hours
         before bed" of the reels). Drinks never move it. */
      /* The drink after the cutoff that matters most tonight (biggest bedtime
         share), or null. Uses the calculator's own per-drink bedtime terms. */
      lateDrink(d, cut) {
        var list = (d.contributions || []).filter(function (c) {
          return c.hour > cut + 1e-6;
        });
        return list.length ? list[0] : null;
      }

      /* "Coffee", not "Drip coffee": the label on the tile they tapped, when
         the drink came from a tile; otherwise the drink's own name. */
      tileLabel(name) {
        var tiles = document.querySelectorAll('.unc-bt__qt[data-bt-add]');
        for (var i = 0; i < tiles.length; i++) {
          if (tiles[i].dataset.btAdd === name) {
            var l = tiles[i].querySelector('.unc-bt__qt-n');
            var t = l ? l.textContent.trim() : '';
            // A generic tile ("Coffee" for "Drip coffee") reads lower case
            // mid-sentence; a brand tile (Red Bull, Coca-Cola) keeps its name.
            if (t) return t !== name ? t.charAt(0).toLowerCase() + t.slice(1) : t;
          }
        }
        return name;
      }

      cutoffOf(d) {
        var off = parseFloat(this.card && this.card.dataset.offset) || 8.8;
        // Tim, 7 Oct: a clean time. Round DOWN to the quarter hour (2:12pm ->
        // 2pm), so the shown cutoff is never later than the maths.
        return Math.floor(((d.bedtime || 23) - off) * 4 + 1e-6) / 4;
      }

      /* When ONE MORE standard coffee would have to land to stay under the
         threshold at bedtime. null when it can't fit, including when that time
         is before a drink they have already had. */
      latestOf(d) {
        if (d.latest === null || d.latest === undefined) return null;
        var last = (d.drinks || []).reduce(function (m, x) {
          return Math.max(m, x.hour || 0);
        }, -1);
        return d.latest < last ? null : d.latest;
      }
    }
  );
})();
